import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import ReusableTable from '../../components/other/ReuseableTable'
import { useGetServicesQuery, useLazyGetServicesQuery } from '../../redux/apis/servicesManagement/sMServicesApiSlice'
import { toast } from 'react-toastify'
import Loader from '../pages/loader'
import EditCreateService from '../../components/services/EditCreateService'
import SearchAndFilterBar from '../../components/other/search/SearchAndFilterBar'
import ServiceCategoriesDropdown from '../../components/ServiceCategoriesDropdown'
import DownloadAllDataButton from '../../components/other/DownloadAllDataButton'
import { can } from '../../helpers/auth'

const ServicesInfo = React.memo(() => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  })
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})
  const context = useContext(AppSettings)
  const { data, isLoading, error, isFetching } = useGetServicesQuery({
    expand: ['service_category', 'last_updated_by'].join(','),
    page: pagination.current,
    page_size: pagination.pageSize,
    ...(search ? { search } : {}),
    ...filters
  })

  const [holder, setHolder] = useState()
  const [edit, setEdit] = useState(false)
  const [create, setCreate] = useState(false)

  let columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    {
      title: 'SERVICE NAME',
      dataIndex: 'service_name',
      key: 'service_name',
      filter: true,
      filterType: 'text'
    },
    {
      title: 'SERVICE ALIAS',
      dataIndex: 'service_alias',
      key: 'service_alias',
      filter: true,
      filterType: 'text'
    },
    {
      title: 'SERVICE TYPE',
      dataIndex: 'service_type',
      key: 'service_type',
      filter: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Customer', value: 'customer' },
        { label: 'Campaign', value: 'campaign' }
      ]
    },
    {
      title: 'SERVICE CATEGORY',
      dataIndex: 'service_category',
      key: 'service_category.category_name',
      filter: true,
      filterComponent: ServiceCategoriesDropdown,
      render: text => <p>{text?.category_name}</p>
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      ellipsis: true,
      render: text => (
        <div
          style={{
            maxWidth: '300px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {text}
        </div>
      )
    },
    {
      title: 'STATUS',
      dataIndex: 'is_active',
      key: 'is_active',
      filter: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
      ],
      render: text => <p>{text ? 'Active' : 'InActive'}</p>
    },
    {
      title: 'TO CRAWL',
      dataIndex: 'to_crawl',
      key: 'to_crawl',
      filter: true,
      filterType: 'select',
      filterOptions: [
        { label: 'True', value: true },
        { label: 'False', value: false }
      ],
      render: text => <p>{text ? 'True' : 'False'}</p>
    },
    {
      title: 'LAST UPDATED BY',
      dataIndex: 'last_updated_by',
      key: 'last_updated_by',
      render: text => (
        <p>
          {text?.first_name} {text?.last_name}
        </p>
      )
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      render: (text, record) => (
        <div className='flex gap-2'>
          {can('change_service') && (
            <button
              className='btn btn-outline-theme'
              onClick={() => handleEdit(record)}
            >
              Edit
            </button>
          )}
        </div>
      )
    }
  ]

  const handleEdit = record => {
    setHolder(record)
    setEdit(true)
  }

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [error])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading || isFetching} />
      {can('add_service') && create && (
        <EditCreateService
          state={'create'}
          open={create}
          setOpen={setCreate}
          record={null}
        />
      )}
      {can('change_service') && edit && (
        <EditCreateService
          state={'edit'}
          open={edit}
          setOpen={setEdit}
          record={holder}
        />
      )}
      <div className='flex'>
        <SearchAndFilterBar
          columns={columns}
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPagination={setPagination}
        />
        <div className='ml-auto flex gap-4'>
          <DownloadAllDataButton
            lazyQueryHook={useLazyGetServicesQuery}
            fileName='services'
          />

          {can('add_service') && (
            <button
              className='btn btn-outline-theme ml-auto mb-4'
              onClick={() => setCreate(true)}
            >
              Create Service
            </button>
          )}
        </div>
      </div>
      <div className='row'>
        <ReusableTable
          columns={columns}
          data={data?.data?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.data?.count,
            showSizeChanger: true
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  )
})

export default ServicesInfo
